import { useCallback, useEffect, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { type Warehouse, type WarehouseLocation } from 'legacy/common/types'
import { useRetailersQuery } from 'legacy/apollo/clientHooks'
import {
  type GetRetailerLocalesMeshQuery,
  useGetRetailerLocalesMeshQuery,
  useGetRetailerQuery,
  useGetAllRetailerLocationsForRetailerQuery,
} from '__codegen__/api'
import { mapWarehouseData } from 'utils/contexts/warehouse/queries/dataUtils'
import {
  makeQueryStaleWhileRevalidate,
  type QueryHook,
} from 'utils/apollo/makeQueryStaleWhileRevalidate'

export const FETCH_WAREHOUSE_QUERY = gql`
  query warehouse($id: ID!) {
    warehouse(id: $id) {
      id
      name
      flippers
      logo {
        url
      }
      orderSources
      country {
        id
        name
      }
    }
  }
`

interface WarehouseWithLocales extends Warehouse {
  locales: GetRetailerLocalesMeshQuery['catalogToolsApiServiceGetLocalesByRetailer']['retailerLocales']
  defaultLocale: GetRetailerLocalesMeshQuery['catalogToolsApiServiceGetLocalesByRetailer']['retailerLocales'][number]
}

export interface WarehouseWithLocations extends WarehouseWithLocales {
  warehouseLocations: WarehouseLocation[]
  inactiveWarehouseLocations: WarehouseLocation[]
}

export interface WarehouseData {
  warehouse: WarehouseWithLocations
}

export interface WarehouseVariables {
  id: string
}

const useCachedFetchWarehouse = makeQueryStaleWhileRevalidate(
  useRetailersQuery.bind(null, FETCH_WAREHOUSE_QUERY) as QueryHook<
    WarehouseData,
    WarehouseVariables
  >,
  { baseCacheKey: 'warehouse' }
)

const useCachedGetRetailerLocalesMesh = makeQueryStaleWhileRevalidate(
  useGetRetailerLocalesMeshQuery,
  { baseCacheKey: 'retailerLocales' }
)

const useCachedGetAllRetailerLocationsForRetailer = makeQueryStaleWhileRevalidate(
  useGetAllRetailerLocationsForRetailerQuery,
  { baseCacheKey: 'retailerLocationsForRetailer' }
)

export const useFetchWarehouse = (variables: WarehouseVariables) => {
  // Modern API data
  const {
    data: retailerData,
    loading: retailerLoading,
    error: retailerError,
    refetch: retailerRefetch,
  } = useGetRetailerQuery({ variables: { retailerId: variables.id } })

  const featureToggles = useMemo(() => retailerData?.retailer?.featureToggles, [retailerData])

  const isCacheEnabled = useMemo(
    () =>
      featureToggles?.some(t => t.name === 'rt_retailer_stale_while_revalidate_enabled') ?? false,
    [featureToggles]
  )

  const partnerName = retailerData?.retailer?.partner?.name

  // Locale data
  const {
    data: localesData,
    error: localesError,
    loading: localesLoading,
    refetch: localesRefetch,
    errorWhileRevalidating: localesRevalidationError,
  } = useCachedGetRetailerLocalesMesh(
    { variables: { retailerId: variables.id } },
    { skipCache: !isCacheEnabled }
  )

  // Retailer location data
  const {
    data: locationsData,
    loading: locationsLoading,
    error: locationsError,
    refetch: locationsRefetch,
    errorWhileRevalidating: locationsRevalidationError,
  } = useCachedGetAllRetailerLocationsForRetailer(
    {
      variables: { retailerId: variables.id },
    },
    { skipCache: !isCacheEnabled }
  )

  // Legacy API data loading (without locations)
  // (This will be removed as soon as all data is loaded from modern API)
  const {
    data: legacyData,
    error: legacyError,
    loading: legacyLoading,
    refetch: legacyRefetch,
    errorWhileRevalidating: legacyWithoutWarehousesRevalidationError,
  } = useCachedFetchWarehouse(
    {
      variables,
    },
    { skipCache: !isCacheEnabled }
  )

  /**
   * Combine the warehouse into a single unit with its locales.
   * This will wait for both locales and the warehouse locales.
   */
  const warehouse = useMemo(() => {
    if (
      !legacyData?.warehouse ||
      !retailerData?.retailer ||
      !locationsData?.retailerLocationSearchArray ||
      localesLoading
    )
      return null

    // Map data from various sources to WarehouseWithLocations shape
    const warehouseData = mapWarehouseData({
      legacyWarehouseData: legacyData.warehouse,
      retailerData: retailerData?.retailer,
      localesData: localesData?.catalogToolsApiServiceGetLocalesByRetailer?.retailerLocales || [],
      locationsData: locationsData?.retailerLocationSearchArray,
    })

    return warehouseData
  }, [retailerData, legacyData, locationsData, localesData, localesLoading])

  const combinedRefetch = useCallback(() => {
    retailerRefetch()
    legacyRefetch()
    locationsRefetch()
    localesRefetch()
  }, [retailerRefetch, legacyRefetch, locationsRefetch, localesRefetch])

  const error = retailerError || legacyError || locationsError || localesError
  const errorWhileRevalidating =
    localesRevalidationError ||
    locationsRevalidationError ||
    legacyWithoutWarehousesRevalidationError

  return {
    warehouse,
    partnerName,
    featureToggles,
    error,
    errorWhileRevalidating,
    loading: retailerLoading || legacyLoading || locationsLoading || localesLoading,
    refetch: combinedRefetch,
  }
}
